import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.formatValue(this.element);
  }

  formatValue(input) {
    const cursorPosition = this.#getCursorPosition(input);
    const digitCountBeforeCursor = this.#getDigitCountBeforePosition(input.value, cursorPosition);
    const formatted = this.#formatPhoneNumber(input.value);

    input.value = formatted;

    const newPosition = this.#calculateCursorPosition(formatted, digitCountBeforeCursor);
    this.#setCursorPosition(input, newPosition);
  }

  input(event) {
    this.formatValue(event.target)
  }

  keydown(event) {
    const input = event.target;
    const key = event.key;
    const cursorPosition = this.#getCursorPosition(input);
    const value = input.value;

    // Allow navigation and control keys
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End'
    ];

    if (allowedKeys.includes(key)) {
      // Handle backspace/delete on non-digit characters (parens, space, dash)
      if (key === 'Backspace' && cursorPosition > 0) {
        const charBeforeCursor = value[cursorPosition - 1];
        if (/\D/.test(charBeforeCursor)) {
          event.preventDefault();
          const digitCountBeforeCursor = this.#getDigitCountBeforePosition(value, cursorPosition);
          const combined = this.#splitAndCombineDigits(value, digitCountBeforeCursor, -1, 0);
          input.value = this.#formatPhoneNumber(combined);

          const newDigitCount = digitCountBeforeCursor - 1;
          const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);
          this.#setCursorPosition(input, newPosition);
          return;
        }
      }

      if (key === 'Delete' && cursorPosition < value.length) {
        const charAtCursor = value[cursorPosition]
        if (/\D/.test(charAtCursor)) {
          event.preventDefault();
          const digitCountBeforeCursor = this.#getDigitCountBeforePosition(value, cursorPosition);
          const combined = this.#splitAndCombineDigits(value, digitCountBeforeCursor, 0, 1);
          input.value = this.#formatPhoneNumber(combined);

          const newDigitCount = digitCountBeforeCursor;
          const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);
          this.#setCursorPosition(input, newPosition);
          return;
        }
      }
      return;
    }

    // Allow modifier keys (needed for paste, etc.)
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    // Prevent non-digit input
    if (!/^\d$/.test(key)) {
      event.preventDefault()
      return;
    }

    // Prevent input if we already have 10 digits (accounting for selection that would be replaced)
    const currentDigits = this.#extractDigits(input.value).length;
    const selectedDigits = this.#getSelectedDigitCount(input);
    const digitsAfterInput = currentDigits - selectedDigits + 1;

    if (digitsAfterInput > 10) {
      event.preventDefault()
      return;
    }
  }

  paste(event) {
    // Handle paste events
    event.preventDefault();

    const input = event.target;
    const cursorPosition = this.#getCursorPosition(input);
    const digitCountBeforeCursor = this.#getDigitCountBeforePosition(input.value, cursorPosition);

    // Get pasted data
    const paste = (event.clipboardData || window.clipboardData).getData('text');

    // Extract digits from pasted content
    let pastedDigits = this.#extractDigits(paste);

    // Split and combine with pasted digits
    const combined = this.#splitAndCombineDigits(input.value, digitCountBeforeCursor, 0, 0, pastedDigits)

    // Format and set value
    input.value = this.#formatPhoneNumber(combined);

    // Set cursor position after the pasted content
    const newDigitCount = digitCountBeforeCursor + pastedDigits.length;
    const newPosition = this.#calculateCursorPosition(input.value, newDigitCount);

    // Use setTimeout to ensure cursor position is set after DOM updates
    setTimeout(() => {
      this.#setCursorPosition(input, newPosition)
    }, 0)
  }


  // Private utility methods
  #extractDigits(value) {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '')
    // Strip leading "1" country code if user typed 11 digits
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1)
    }
    return digits
  }

  #limitToMaxDigits(digits, maxDigits = 10) {
    // Limit digits to maximum length
    return digits.length > maxDigits ? digits.slice(0, maxDigits) : digits
  }

  #splitAndCombineDigits(value, digitCountBeforeCursor, beforeOffset = 0, afterOffset = 0, newDigits = '') {
    // Extract digits and split at cursor position with offsets
    const digits = this.#extractDigits(value);
    const beforeCursor = digits.slice(0, digitCountBeforeCursor + beforeOffset);
    const afterCursor = digits.slice(digitCountBeforeCursor + afterOffset);
    const combined = this.#limitToMaxDigits(beforeCursor + newDigits + afterCursor);
    return combined;
  }

  #formatPhoneNumber(value) {
    const digits = this.#limitToMaxDigits(this.#extractDigits(value));

    // Format as (XXX) XXX-XXXX
    let formatted = '';
    if (digits.length > 0) {
      formatted = '(' + digits.slice(0, 3);
    }
    if (digits.length >= 3) {
      formatted += ') ';
    }
    if (digits.length > 3) {
      formatted += digits.slice(3, 6);
    }
    if (digits.length > 6) {
      formatted += '-' + digits.slice(6, 10);
    }

    return formatted;
  }

  #getCursorPosition(input) {
    return input.selectionStart || 0;
  }

  #getSelectedDigitCount(input) {
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const selectedText = input.value.slice(start, end);
    return this.#extractDigits(selectedText).length;
  }

  #setCursorPosition(input, position) {
    input.setSelectionRange(position, position);
  }

  #getDigitCountBeforePosition(value, position) {
    // Count how many digits appear before the cursor position
    return this.#extractDigits(value.slice(0, position)).length;
  }

  #calculateCursorPosition(formattedValue, targetDigitCount) {
    // Calculate cursor position based on digit count in formatted string
    let newPosition = 0;
    let digitCount = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitCount++
        if (digitCount === targetDigitCount) {
          // Place cursor after this digit
          newPosition = i + 1;
          break
        }
      }
    }

    // If cursor should be at the end (all digits), place it at the end of formatted string
    if (targetDigitCount >= this.#extractDigits(formattedValue).length) {
      newPosition = formattedValue.length;
    }

    return newPosition;
  }
}
